// Notificaciones in-app (campana): estado, badge de no leídas y tiempo real por Socket.io.
// La suscripción Web Push (VAPID) vive en PushService.
import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { io, Socket } from 'socket.io-client';
import { BehaviorSubject, Subject, firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { GameInvitation, PresenceUser } from '../features/games/models/game.models';

export interface UserNotification {
  id: string;
  type: 'karma_received' | 'game_invite' | string;
  title: string;
  body: string | null;
  url: string | null;
  payload: any;
  readAt: string | null;
  createdAt: string;
}

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly http = inject(HttpClient);
  private socket: Socket | null = null;

  private readonly itemsSubject = new BehaviorSubject<UserNotification[]>([]);
  /** Lista de notificaciones (más recientes primero). */
  readonly items$ = this.itemsSubject.asObservable();

  private readonly unreadSubject = new BehaviorSubject<number>(0);
  /** Cantidad de no leídas (badge). */
  readonly unreadCount$ = this.unreadSubject.asObservable();

  private readonly incomingSubject = new Subject<UserNotification>();
  /** Cada notificación que llega en vivo (para el toast). */
  readonly incoming$ = this.incomingSubject.asObservable();

  private readonly gameInviteSubject = new Subject<GameInvitation>();
  /** Invitaciones a Pausa Lúdica que llegan en vivo, estés en la página que estés. */
  readonly gameInvite$ = this.gameInviteSubject.asObservable();

  private readonly onlineUsersSubject =new BehaviorSubject<PresenceUser[]>([]);
  /** Compañeros conectados ahora (misma empresa; B2C solo ve B2C). */
  readonly onlineUsers$ = this.onlineUsersSubject.asObservable();

  private get apiUrl(): string {
    return `${environment.apiUrl}/notifications`;
  }

  private get socketUrl(): string {
    const api = environment.apiUrl || (typeof window !== 'undefined' ? `${window.location.origin}/api` : '');
    return api.replace(/\/api$/, '');
  }

  async loadFeed(): Promise<void> {
    try {
      const res = await firstValueFrom(
        this.http.get<{ notifications: UserNotification[] }>(`${this.apiUrl}/feed`)
      );
      const items = res.notifications || [];
      this.itemsSubject.next(items);
      this.unreadSubject.next(items.filter((n) => !n.readAt).length);
    } catch {
      // silencioso: la campana no debe romper la app
    }
  }

  /** Una página del historial completo (para la página de Notificaciones); no toca el estado de la campana. */
  async fetchPage(limit: number, offset: number): Promise<{ notifications: UserNotification[]; total: number }> {
    const res = await firstValueFrom(
      this.http.get<{ notifications: UserNotification[]; total: number }>(`${this.apiUrl}/feed`, {
        params: { limit, offset }
      })
    );
    return { notifications: res.notifications || [], total: res.total || 0 };
  }

  async refreshUnread(): Promise<void> {
    try {
      const res = await firstValueFrom(this.http.get<{ count: number }>(`${this.apiUrl}/unread-count`));
      this.unreadSubject.next(res.count || 0);
    } catch {
      // silencioso
    }
  }

  async markRead(id: string): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${this.apiUrl}/${id}/read`, {}));
      const now = new Date().toISOString();
      this.itemsSubject.next(this.itemsSubject.value.map((n) => (n.id === id ? { ...n, readAt: now } : n)));
      this.unreadSubject.next(Math.max(this.unreadSubject.value - 1, 0));
    } catch {
      // silencioso
    }
  }

  async markAllRead(): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`${this.apiUrl}/read-all`, {}));
      const now = new Date().toISOString();
      this.itemsSubject.next(this.itemsSubject.value.map((n) => ({ ...n, readAt: n.readAt || now })));
      this.unreadSubject.next(0);
    } catch {
      // silencioso
    }
  }

  /**
   * Conecta al socket y escucha `notification:received` y `presence:update` en tiempo real.
   * `profile` viaja en el handshake para que los compañeros vean tu nombre y avatar reales.
   */
  connectRealtime(profile?: { displayName?: string; avatarPreset?: string; photoUrl?: string | null }): void {
    const token = localStorage.getItem('sb_access_token') || localStorage.getItem('sb_token') || '';
    if (!token || this.socket) return;

    try {
      this.socket = io(this.socketUrl, {
        path: '/socket.io',
        // Función: cada reconexión usa el token vigente.
        auth: (cb: (data: Record<string, unknown>) => void) => {
          cb({
            token: localStorage.getItem('sb_access_token') || localStorage.getItem('sb_token') || token,
            ...(profile?.displayName ? { displayName: profile.displayName } : {}),
            ...(profile?.avatarPreset ? { avatarPreset: profile.avatarPreset } : {}),
            ...(profile?.photoUrl ? { photoUrl: profile.photoUrl } : {})
          });
        },
        transports: ['websocket', 'polling']
      });

      this.socket.on('connect', () => {
        this.socket?.emit('presence:get', (users: PresenceUser[]) => {
          if (Array.isArray(users)) this.onlineUsersSubject.next(users);
        });
      });

      this.socket.on('presence:update', (users: PresenceUser[]) => {
        this.onlineUsersSubject.next(Array.isArray(users) ? users : []);
      });

      this.socket.on('game:invitation_received', (invite: GameInvitation) => {
        if (invite?.roomCode) this.gameInviteSubject.next(invite);
      });

      this.socket.on('notification:received', (n: UserNotification) => {
        if (!n?.id) return;
        this.itemsSubject.next([n, ...this.itemsSubject.value]);
        if (!n.readAt) {
          this.unreadSubject.next(this.unreadSubject.value + 1);
        }
        this.incomingSubject.next(n);
      });
    } catch {
      this.socket = null;
    }
  }

  disconnect(): void {
    this.socket?.disconnect();
    this.socket = null;
    this.itemsSubject.next([]);
    this.unreadSubject.next(0);
    this.onlineUsersSubject.next([]);
  }
}
